import { computed } from "vue";
import { useComponentContext } from "../../composables/useComponentContext";
import { useStateStore } from "../../composables/useStateStore";
import type { TableProps } from "./TableProps";
import type { TableHeaderCellProps } from "./TableHeaderCellProps";
import type { TableContext } from "./useTable.types";
import {
  TABLE_TYPE,
  TABLE_HEAD_TYPE,
  TABLE_BODY_TYPE,
  TABLE_ROW_TYPE,
  TABLE_CELL_TYPE,
  TABLE_HEADER_CELL_TYPE,
} from "./useTable.types";

/** Resolve the column index for the current component, accounting for colspan. */
const resolveColumnIndex = (
  context: ReturnType<typeof useComponentContext>,
  stateStore: ReturnType<typeof useStateStore<TableProps>>,
): number => {
  if (context.uid === null) return 0;

  const parentRow = stateStore.findParentOfType(context.uid, TABLE_ROW_TYPE);
  if (!parentRow?.uid) return 0;

  const cells = stateStore.findChildrenByType<TableHeaderCellProps>(parentRow.uid, TABLE_HEADER_CELL_TYPE);
  if (cells.length > 0) {
    let index = 0;
    for (const cell of cells) {
      if (cell.uid === context.uid) return index;
      index += cell.props?.colspan ?? 1;
    }
    return 0;
  }

  const bodyCells = stateStore.findChildrenByType(parentRow.uid, TABLE_CELL_TYPE);
  return bodyCells.findIndex((cell) => cell.uid === context.uid);
};

/**
 * Creates the shared table context.
 *
 * Resolves the parent table, head/body UIDs, header cells, and the
 * current component's row/column index. The returned `TableContext` is
 * passed to every sub-composable so they all share the same reactive
 * references.
 *
 * **This should only be called once per `useTable()` invocation.**
 */
export const useTableContext = (): TableContext => {
  const context = useComponentContext();
  const stateStore = useStateStore<TableProps>();

  // ─── Table resolution ────────────────────────────────────────────────

  const tableUid = computed(() => {
    if (context.typeName === TABLE_TYPE) return context.uid;
    if (context.uid === null) return null;
    return stateStore.findParentOfType(context.uid, TABLE_TYPE)?.uid ?? null;
  });

  const tableProps = computed(() => {
    if (!tableUid.value) return;
    return stateStore.getByUid<TableProps>(tableUid.value)?.props;
  });

  const headUid = computed(() => {
    if (!tableUid.value) return null;
    return stateStore.findChildrenByType(tableUid.value, TABLE_HEAD_TYPE)[0]?.uid ?? null;
  });

  const bodyUid = computed(() => {
    if (!tableUid.value) return null;
    return stateStore.findChildrenByType(tableUid.value, TABLE_BODY_TYPE)[0]?.uid ?? null;
  });

  // ─── Header cells ────────────────────────────────────────────────────

  const headerCells = computed(() => {
    if (!headUid.value) return [];

    const firstRow = stateStore.findChildrenByType(headUid.value, TABLE_ROW_TYPE)[0];
    if (!firstRow?.uid) return [];

    return stateStore.findChildrenByType<TableHeaderCellProps>(firstRow.uid, TABLE_HEADER_CELL_TYPE);
  });

  // ─── Index calculations ──────────────────────────────────────────────

  const currentRowIndex = computed(() => {
    if (context.uid === null || !bodyUid.value) return -1;

    const rows = stateStore.findChildrenByType(bodyUid.value, TABLE_ROW_TYPE);
    return rows.findIndex((row) => row.uid === context.uid);
  });

  const currentColumnIndex = computed(() => resolveColumnIndex(context, stateStore));

  return {
    context,
    stateStore,
    tableUid,
    tableProps,
    headUid,
    bodyUid,
    headerCells,
    currentRowIndex,
    currentColumnIndex,
  };
};
